import { create } from 'zustand';
import type { StockUpdateRow } from '../../hooks/useCsvParser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WizardStep = 'upload' | 'validation' | 'preview' | 'processing' | 'result';

export interface ValidationError {
  row: number;
  sku: string;
  reason: string;
  quantity?: number;
  /**
   * Phase 92: a machine code for a refusal the UI translates itself. Today
   * only `'supply_alias'` (the row is a client product built on the 3PL's
   * blank); `ErrorTable` shows the translated reason instead of `reason`.
   */
  reasonCode?: string;
}

export interface PreviewRow {
  sku: string;
  currentStock: number;
  qtyToAdd: number;
  newTotal: number;
  unitCost: number | null;
  totalCostImpact: number | null;
  isNewAtLocation: boolean;
  skuId?: string;
}

export interface ProcessingResult {
  batchId: string;
  successCount: number;
  failedCount: number;
  validationErrors: ValidationError[];
  executionErrors: string[];
  mode?: 'complete' | 'partial' | 'blocked' | 'network_error';
  /** BLU-1268: rows on a Just in time pair. The server moves nothing for them and counts them apart. */
  skippedJustInTime?: number;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface BulkStockUploadState {
  step: WizardStep;
  file: File | null;
  parsedRows: StockUpdateRow[];
  validationErrors: ValidationError[];
  blockOnErrors: boolean;
  /** Operator reference / order number for the adjustment-document header. */
  reference: string;
  previewRows: PreviewRow[];
  skippedRows: StockUpdateRow[];
  processing: { total: number; processed: number; failed: number };
  result: ProcessingResult | null;
}

interface BulkStockUploadActions {
  setStep: (step: WizardStep) => void;
  setFile: (file: File | null) => void;
  setParsedRows: (rows: StockUpdateRow[]) => void;
  setValidationErrors: (errors: ValidationError[]) => void;
  setBlockOnErrors: (block: boolean) => void;
  setReference: (reference: string) => void;
  setPreviewRows: (rows: PreviewRow[]) => void;
  setSkippedRows: (rows: StockUpdateRow[]) => void;
  startProcessing: (total: number) => void;
  tickProcessed: () => void;
  tickFailed: () => void;
  setResult: (result: ProcessingResult) => void;
  clearResult: () => void;
  reset: () => void;
}

type BulkStockUploadStore = BulkStockUploadState & BulkStockUploadActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: BulkStockUploadState = {
  step: 'upload',
  file: null,
  parsedRows: [],
  validationErrors: [],
  blockOnErrors: true,
  reference: '',
  previewRows: [],
  skippedRows: [],
  processing: { total: 0, processed: 0, failed: 0 },
  result: null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useBulkStockUpload = create<BulkStockUploadStore>((set) => ({
  ...initialState,

  setStep: (step) => set({ step }),

  setFile: (file) => set({ file }),

  setParsedRows: (rows) => set({ parsedRows: rows }),

  setValidationErrors: (errors) => set({ validationErrors: errors }),

  setBlockOnErrors: (block) => set({ blockOnErrors: block }),

  setReference: (reference) => set({ reference }),

  setPreviewRows: (rows) => set({ previewRows: rows }),

  setSkippedRows: (rows) => set({ skippedRows: rows }),

  startProcessing: (total) =>
    set({
      step: 'processing',
      processing: { total, processed: 0, failed: 0 },
    }),

  tickProcessed: () =>
    set((s) => ({
      processing: {
        ...s.processing,
        processed: s.processing.processed + 1,
      },
    })),

  tickFailed: () =>
    set((s) => ({
      processing: {
        ...s.processing,
        processed: s.processing.processed + 1,
        failed: s.processing.failed + 1,
      },
    })),

  setResult: (result) => set({ step: 'result', result }),

  clearResult: () => set({ result: null }),

  reset: () => set(initialState),
}));
